import axios from "axios";

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const MODULE_NAME = "links_sociallinks";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";

// Module fields that we work with
const SOCIAL_LINKS_MODULE_FIELDS = [
  "id",
  "name",
  "parentid_c",
  "type",
  "link",
  "date_entered",
  "date_modified",
  "deleted",
];

/**
 * Filter outgoing payload to include only module fields
 */
const filterSocialLinksPayload = (data = {}) => {
  const payload = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (normalizedValue === "") {
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        return;
      }
    }

    if (SOCIAL_LINKS_MODULE_FIELDS.includes(key)) {
      payload[key] = normalizedValue;
    }
  });

  return payload;
};

/**
 * Process social links data entries
 */
const processSocialLinksData = (entryList) => {
  if (!Array.isArray(entryList)) return [];

  return entryList.map((entry) => {
    if (!entry || !entry.name_value_list) return null;

    const values = {};
    Object.entries(entry.name_value_list).forEach(([key, item]) => {
      values[key] = item?.value ?? "";
    });

    const filteredValues = {};
    SOCIAL_LINKS_MODULE_FIELDS.forEach((field) => {
      if (values[field] !== undefined && values[field] !== null) {
        filteredValues[field] = values[field];
      }
    });

    return {
      id: entry.id,
      ...filteredValues,
    };
  }).filter(Boolean);
};

/**
 * Fetch all social links data
 */
export const fetchSocialLinksData = async (sessionId) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      select_fields: SOCIAL_LINKS_MODULE_FIELDS,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    
    if (response?.data?.name === "Invalid Session ID" || response?.data?.number) {
      return [];
    }
    
    if (response?.data?.description && response.data.description.includes("does not exist")) {
      return [];
    }
    
    const entryList = response?.data?.entry_list || [];

    return processSocialLinksData(entryList);
  } catch (error) {
    console.error("Error fetching social links:", error);
    if (error.response?.status === 500) {
      return [];
    }
    return [];
  }
};

/**
 * Fetch social links by parent ID (contact ID)
 */
export const fetchSocialLinksByParent = async (sessionId, parentId) => {
  if (!sessionId || !parentId) return [];

  try {
    const rest_data = {
      session: sessionId,
      module_name: MODULE_NAME,
    };

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(JSON.stringify(rest_data))}`;
    const response = await axios.get(fullUrl);
    
    if (response?.data?.name === "Invalid Session ID" || response?.data?.number) {
      return [];
    }
    
    if (response?.data?.description && response.data.description.includes("does not exist")) {
      return [];
    }
    
    const entryList = response?.data?.entry_list || [];
    const processedData = processSocialLinksData(entryList);
    
    const filtered = processedData.filter(entry => 
      entry.parentid_c === parentId || entry.parentid_c === String(parentId)
    );
    
    return filtered;
  } catch (error) {
    console.error("Error fetching social links by parent:", error);
    if (error.response?.status === 500) {
      return [];
    }
    return [];
  }
};

/**
 * Add new social link entry
 */
export const addSocialLinkData = async (sessionId, data) => {
  try {
    const filteredPayload = filterSocialLinksPayload(data);

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: filteredPayload,
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    return response.data;
  } catch (error) {
    console.error("Error adding social link:", error);
    if (error.response?.status === 500) {
      throw new Error("Social Links module not found in backend. Please contact administrator.");
    }
    throw error;
  }
};

/**
 * Edit existing social link entry
 */
export const editSocialLinkData = async (sessionId, id, data) => {
  try {
    const dataWithId = { ...data, id };
    const filteredPayload = filterSocialLinksPayload(dataWithId);

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: filteredPayload,
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    return response.data;
  } catch (error) {
    console.error("Error editing social link:", error);
    throw error;
  }
};

/**
 * Soft delete social link entry
 */
export const deleteSocialLinkData = async (sessionId, id) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: {
        id,
        deleted: "1",
      },
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    return response.data;
  } catch (error) {
    console.error("Error deleting social link:", error);
    throw error;
  }
};

/**
 * Sync social links entries - handles add/edit/delete operations
 */
export const syncSocialLinksEntries = async (
  sessionId,
  parentId,
  entries,
  removedIds = []
) => {
  try {
    // Delete removed entries
    for (const id of removedIds) {
      if (id && id.trim() !== "") {
        await deleteSocialLinkData(sessionId, id);
      }
    }

    // Add or edit entries
    for (const entry of entries) {
      const linkData = {
        type: entry.type || "",
        link: entry.link || "",
        name: entry.name || `${entry.type} Link` || "Social Link",
        parentid_c: parentId,
      };

      const hasCoreRequiredFields =
        linkData.type && linkData.type.trim().length > 0 &&
        linkData.link && linkData.link.trim().length > 0;

      if (hasCoreRequiredFields) {
        if (entry.id && entry.id.trim() !== "") {
          await editSocialLinkData(sessionId, entry.id, linkData);
        } else {
          await addSocialLinkData(sessionId, linkData);
        }
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error syncing social links:", error);
    throw error;
  }
};

export default {
  fetchSocialLinksData,
  fetchSocialLinksByParent,
  addSocialLinkData,
  editSocialLinkData,
  deleteSocialLinkData,
  syncSocialLinksEntries,
};
